'use client';

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Cookie from 'js-cookie';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import AddedToCartPopup from '@/components/V2/Popup/AddedToCart';
import AddedToExistingEzShipPopup from '@/components/V2/Popup/AddToExistingEzShip';
import { BYP_KIT_IDS } from '@/helpers/constants/data';
import { PaymentType, ShoppingFlowType } from '@/helpers/constants/shopping';
import useEzShip from '@/helpers/hooks/useEzShip';
import usePageLoaded from '@/helpers/hooks/usePageLoaded';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import { ADD_TO_CART_TOAST } from '@/layouts/ToastWrapper/containerIds';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  useAddFoodLabelToEzShipItemMutation,
  useAddToExistingEzShipMutation,
  useAddToEzShipMutation,
  useGetEzShipCartItemsQuery,
  useRemoveItemFromEzShipCartMutation,
  useUpdateProductQuantityInAutoOrderProductsListMutation,
} from '@/store/api/ezShip';
import {
  useAddListToCartMutation,
  useAddToCartMutation,
  useAddToCartUsingPawPointsMutation,
  useAddToCartUsingSampleCreditsMutation,
  useApplyFoodLabelMutation,
  useGetShoppingCartQuery,
  useRemoveFromCartMutation,
  useRemoveListFromCartMutation,
  useSetShoppingCartTypeMutation,
  useUpdateEZShipMutation,
  useUpdateListEZShipMutation,
  useUpdateQuantityMutation,
} from '@/store/api/shoppingCart';
import { removeAction, setAction, setFoodLabelStatus } from '@/store/slices/ezship';
import { AddToCart, AddToCartToast, CartItem } from '@/types/Cart';
import { ResponseError } from '@/types/Error';
import {
  AddToExistingEzShipPayload,
  AddToEzshipPayload,
  UpdateProductQuantityInAutoOrderPayload,
} from '@/types/EzShip';
import { ApplyFoodLabelPayload } from '@/types/FoodLabel';
import { ProductStrapi, ProductStrapiOfEnrollment } from '@/types/Product';
import { SuccessResponse } from '@/types/Success';

interface ShoppingCartContextProps {
  cart: Array<CartItem> | undefined;
  couponCode: string | null | undefined;
  // giftCard: number | string | null;
  giftCardNumber: string | null | undefined;
  totalItemsInCart: number | undefined;
  totalPrice?: number | string;
  totalPawPoints?: number | string;
  totalSampleCredits?: number | string;
  hasItemWithPawPoints: boolean | undefined;
  hasItemWithSampleCredits: boolean | undefined;
  ezShipItems: Array<CartItem> | undefined;
  totalEZShipItems: number | undefined;
  ezShipTotalPrice: number | string | undefined;
  qualifiedItemsCount: number | undefined;
  isAddingToCart: boolean;
  isAddingToCartUsingPawPoints: boolean;
  isAddingToCartUsingSampleCredits: boolean;
  isApplyingLabel: boolean;
  isAddingToExistingEzShip: boolean;
  isLoading: boolean;
  isLoadingShoppingCart: boolean;
  isUpdatingEzShipCartQuantity: boolean;
  isCartStable: boolean;
  showChooseYourCheckoutFlowModal: boolean;
  isEnrollmentFlow: boolean;
  onAddToCart: (product: ProductStrapi, quantity: number, isEZShip: boolean, algoliaQueryID?: string) => Promise<void>;
  onAddToCartWithItemCode: (
    itemCode: string,
    quantity: number,
    isEZShip: boolean,
    algoliaQueryID?: string
  ) => Promise<SuccessResponse<unknown>>;
  onAddProductsListToCartForEnrollment: (
    product: ProductStrapiOfEnrollment[],
    isEZShip: boolean,
    algoliaQueryID?: string
  ) => Promise<void>;
  onAddProductsListToCart: (
    product: ProductStrapiOfEnrollment[],
    quantity: number,
    isEZShip: boolean,
    algoliaQueryID?: string
  ) => Promise<void>;
  onAddToCartUsingPawPoints: (
    product: ProductStrapi,
    quantity: number,
    isEZShip: boolean,
    algoliaQueryID?: string
  ) => Promise<void>;
  onAddToCartUsingSampleCredits: (
    product: ProductStrapi,
    quantity: number,
    isEZShip: boolean,
    algoliaQueryID?: string
  ) => Promise<void>;
  onUpdateQuantity: (id: string, quantity: number) => Promise<void>;
  onUpdateEZShip: (id: string, isEZShip: boolean) => Promise<void>;
  onRemoveFromCart: (id: string) => Promise<void>;
  onApplyFoodLabel: (payload: ApplyFoodLabelPayload) => Promise<void>;
  onAddToAutoOrder: (payload: AddToEzshipPayload) => Promise<void>;
  onUpdateEzShipCartQuantity: (payload: UpdateProductQuantityInAutoOrderPayload) => Promise<void>;
  onRemoveFromEzShip: (payload: { autoOrderId: string; itemCode: string }) => Promise<void>;
  onAddToExistingEzShip: (
    payload: AddToExistingEzShipPayload,
    product: ProductStrapi,
    autoOrderTitle: string | undefined,
    success: () => void
  ) => Promise<void>;

  onRefetchEzShipCartItems: () => void;
  onRemoveListFromCart: (ids: string[], removeForcefully?: boolean) => Promise<void>;
  onUpdateListOfProductEZShip: (itemList: { ids: string[]; isEZShip: boolean }) => Promise<void>;
  setShowChooseYourCheckoutFlowModal: React.Dispatch<React.SetStateAction<boolean>>;
  shoppingFlowType: number | null | undefined;
  updateShoppingFlowType: (newType: ShoppingFlowType) => void;
}

interface ShoppingCartProviderProps {
  children: ReactNode;
}

const ShoppingCartContext = createContext<ShoppingCartContextProps>({
  cart: undefined,
  couponCode: null,
  giftCardNumber: null,
  ezShipItems: undefined,
  totalEZShipItems: 0,
  ezShipTotalPrice: 0,
  totalItemsInCart: 0,
  totalPrice: 0,
  totalPawPoints: 0,
  totalSampleCredits: 0,
  qualifiedItemsCount: 0,
  hasItemWithPawPoints: false,
  hasItemWithSampleCredits: false,
  isAddingToCart: false,
  isAddingToCartUsingPawPoints: false,
  isAddingToCartUsingSampleCredits: false,
  isAddingToExistingEzShip: false,
  isCartStable: false,
  isApplyingLabel: false,
  isLoading: false,
  isLoadingShoppingCart: false,
  isUpdatingEzShipCartQuantity: false,

  showChooseYourCheckoutFlowModal: false,
  isEnrollmentFlow: false,
  onAddToCart: async (product: ProductStrapi, quantity: number, isEZShip: boolean) => {},
  onAddToCartWithItemCode: async (itemCode: string, quantity: number, isEZShip: boolean, algoliaQueryID?: string) => {
    return {} as SuccessResponse<unknown>;
  },
  onAddProductsListToCart: async (product: ProductStrapiOfEnrollment[], quantity: number, isEZShip: boolean) => {},
  onAddProductsListToCartForEnrollment: async (product: ProductStrapiOfEnrollment[], isEZShip: boolean) => {},
  onAddToCartUsingPawPoints: async (product: ProductStrapi, quantity: number, isEZShip: boolean) => {},
  onAddToCartUsingSampleCredits: async (product: ProductStrapi, quantity: number, isEZShip: boolean) => {},
  onUpdateQuantity: async (id: string, quantity: number) => {},
  onUpdateEZShip: async (id: string, isEZShip: boolean) => {},
  onUpdateListOfProductEZShip: async (itemList: { ids: string[]; isEZShip: boolean }) => {},
  onRemoveFromCart: async (id: string) => {},
  onApplyFoodLabel: async (payload: ApplyFoodLabelPayload) => {},
  onAddToAutoOrder: async (payload: AddToEzshipPayload) => {},
  onUpdateEzShipCartQuantity: async (payload: UpdateProductQuantityInAutoOrderPayload) => {},
  onRemoveFromEzShip: async (payload: { autoOrderId: string; itemCode: string }) => {},
  onAddToExistingEzShip: async (
    payload: AddToExistingEzShipPayload,
    product: ProductStrapi,
    autoOrderTitle: string | undefined,
    success: () => void
  ) => {},
  onRefetchEzShipCartItems: () => {},
  onRemoveListFromCart: async (ids: string[], removeForcefully?: boolean) => {},
  setShowChooseYourCheckoutFlowModal: () => {},
  shoppingFlowType: null,
  updateShoppingFlowType: () => {},
});

const ShoppingCartProvider = ({ children }: ShoppingCartProviderProps) => {
  const isPageLoaded = usePageLoaded();
  const { ezShipId } = useEzShip();

  const isEzShipMode = !isNaN(parseInt(ezShipId));

  const {
    totalItemsInCart,
    ezShipItems,
    totalPrice,
    totalPawPoints,
    totalSampleCredits,
    hasItemWithSampleCredits,
    hasItemWithPawPoints,
    totalEZShipItems,
    ezShipTotalPrice,
    cart,
    giftCardNumber,
    couponCode,
    isLoadingShoppingCart,
    qualifiedItemsCount,
    shoppingFlowType,
  } = useGetShoppingCartQuery(undefined, {
    selectFromResult: ({ data, isLoading, isFetching }) => ({
      ...data?.data,
      ezShipItems: data?.data.cart.filter(({ isEZShip }) => isEZShip),
      isLoadingShoppingCart: isLoading || isFetching,
    }),
    skip: !isPageLoaded || isEzShipMode,
  });

  const { setVisibilityOfEzShipSidebar } = useAppContext();

  const [addToCart, { isLoading: isAddingToCart }] = useAddToCartMutation();
  const [addListToCart, { isLoading: isAddingProductListToCart }] = useAddListToCartMutation();
  const [addToCartUsingPawPoints, { isLoading: isAddingToCartUsingPawPoints }] = useAddToCartUsingPawPointsMutation();
  const [addToCartUsingSampleCredits, { isLoading: isAddingToCartUsingSampleCredits }] =
    useAddToCartUsingSampleCreditsMutation();
  const [updateEZShip, { isLoading: isUpdatingEZShip }] = useUpdateEZShipMutation();
  const [updateListEZShip, { isLoading: isUpdatingListEZShip }] = useUpdateListEZShipMutation();
  const [updateQuantity, { isLoading: isUpdatingQuantity }] = useUpdateQuantityMutation();
  const [removeFromCart, { isLoading: isRemovingFromCart }] = useRemoveFromCartMutation();
  const [removeListFromCart, { isLoading: isRemovingListFromCart }] = useRemoveListFromCartMutation();
  const [applyFoodLabel, { isLoading: isApplyingLabel }] = useApplyFoodLabelMutation();
  const [applyFoodLabelToEzShipItem, applyFoodLabelResp] = useAddFoodLabelToEzShipItemMutation();

  const [addToEzship, addToEzshipResp] = useAddToEzShipMutation();
  const [updateEzShipCartQuantity, { isLoading: isUpdatingEzShipCartQuantity }] =
    useUpdateProductQuantityInAutoOrderProductsListMutation();
  const [removeFromEzShip, removeFromEzShipResp] = useRemoveItemFromEzShipCartMutation();
  const [setShoppingCartType] = useSetShoppingCartTypeMutation();
  const [showChooseYourCheckoutFlowModal, setShowChooseYourCheckoutFlowModal] = useState(false);

  const { isEzshipCartLoading, refetch: refetchEzShipCartItems } = useGetEzShipCartItemsQuery(
    { autoOrderId: ezShipId },
    {
      skip: !ezShipId || +ezShipId == 0,
      selectFromResult: ({ isLoading, isFetching, data }) => ({
        isEzshipCartLoading: isLoading || isFetching,
      }),
    }
  );

  const [addToExistingEzShip, { isLoading: isAddingToExistingEzShip }] = useAddToExistingEzShipMutation();
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const { webalias } = useWebAlias();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const currentEzShip = useAppSelector(store => store.slices.ezShipSlice.currentEzShip);
  const notification = useRef<SuccessResponse<unknown> | null>(null);
  const addToCartNotification = useRef<SuccessResponse<AddToCartToast> | null>(null);
  // const [action, setAction] = useState<Action>({ name: '', payload: null });

  const isLoading =
    isLoadingShoppingCart ||
    isAddingToCart ||
    isAddingToCartUsingPawPoints ||
    isAddingToCartUsingSampleCredits ||
    isUpdatingEZShip ||
    isUpdatingQuantity ||
    isRemovingFromCart ||
    isApplyingLabel ||
    isEzshipCartLoading ||
    addToEzshipResp.isLoading ||
    isAddingToExistingEzShip ||
    isUpdatingEzShipCartQuantity;

  // const isAddingItemToAutoOrder = action.name === 'addToEzShip';
  // const isRemovingItemFromAutoOrder = action.name === 'removeEzShipFromCart';
  // const isUpdatingAutoOrderItem = action.name === 'updateEzShipCartQuantity';

  const showLoader = useMemo(() => {
    const paths = ['/checkout/preorder', '/thank-you'];
    const isIncluded = paths.some(path => pathName.includes(path));

    return isLoading && !isIncluded;
  }, [isLoading, pathName]);

  const isCartStable = useMemo(() => {
    return !isLoadingShoppingCart && !isLoading;
  }, [isLoading, isLoadingShoppingCart]);

  const ezShipFoodLabelStatus = useAppSelector(state => state.slices.ezShipSlice.foodLabel);

  const isEnrollmentFlow = useMemo(() => !!cart?.find(k => BYP_KIT_IDS.includes(`${k.id}`)), [cart]);

  const updateShoppingFlowType = useCallback(
    (newType: ShoppingFlowType) => {
      if (!shoppingFlowType || newType < shoppingFlowType) {
        setShoppingCartType(newType)
          .unwrap()
          .catch(() => {});
      }
    },
    [shoppingFlowType, setShoppingCartType]
  );

  useEffect(() => {
    if (
      applyFoodLabelResp.isLoading !== ezShipFoodLabelStatus?.isLoading ||
      applyFoodLabelResp.isSuccess !== ezShipFoodLabelStatus?.isComplete
    ) {
      dispatch(
        setFoodLabelStatus({ isLoading: applyFoodLabelResp.isLoading, isComplete: applyFoodLabelResp.isSuccess })
      );
    }
  }, [applyFoodLabelResp, dispatch, ezShipFoodLabelStatus]);

  const handleAddToCart = useCallback(
    async (product: ProductStrapi, quantity: number, isEZShip: boolean, algoliaQueryID?: string) => {
      const clientIpAddress = Cookie.get('clientIpAddress');
      const existingCartItem = cart?.find(c => c.id === product?.attributes?.ItemCode);
      const existingAlgoliaQueryID = existingCartItem?.algoliaQueryID?.split(',') || [];
      const item: AddToCart = {
        itemCode: product?.attributes?.ItemCode || '',
        quantity,
        isEzShip: isEZShip,
        algoliaQueryID: [...new Set([algoliaQueryID || '', ...existingAlgoliaQueryID])].toString(),
      };

      if (isEZShip) {
        item.numberOfEZShipDays = 7;
      }

      if (webalias) {
        item.webAlias = webalias;
      }

      if (clientIpAddress) {
        item.clientIpAddress = clientIpAddress;
      }

      // setModalConfig(state => ({ ...state, isOpen: false }));

      await addToCart(item)
        .unwrap()
        .then(async res => {
          // setKlaviyoCartData(mapDataToAddedToCart(cart || [], cartItem, window.location.origin));
          const toastData: AddToCartToast = {
            name: product.attributes.ItemName,
            image: product.attributes.MainImage,
            quantity: item.quantity,
            weight: product.attributes.Weight,
            price: product.attributes.Prices?.RetailPrice ?? 0,
            paymentType: PaymentType.CREDIT_CARD,
          };

          const newResponse: SuccessResponse<AddToCartToast> = {
            message: res.message,
            exceptionMessage: res.exceptionMessage,
            success: res.success,
            data: toastData,
          };

          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            addToCartNotification.current = newResponse;
          }

          // if (res.success) {
          //   const AddedToCartPopup = (await import('@/components/Popup/AddedToCart')).default;
          //   toast(({ closeToast }) => <AddedToCartPopup closeToast={closeToast} product={product} />, {
          //     containerId: ADD_TO_CART_TOAST,
          //   });
          // } else {
          //   toast(res.message, { type: 'error' });
          // }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [addToCart, cart, webalias]
  );

  const handleAddToCartWithItemCode = useCallback(
    async (itemCode: string, quantity: number, isEZShip: boolean, algoliaQueryID?: string) => {
      const clientIpAddress = Cookie.get('clientIpAddress');
      const existingCartItem = cart?.find(c => c.id === itemCode);
      const existingAlgoliaQueryID = existingCartItem?.algoliaQueryID?.split(',') || [];
      const item: AddToCart = {
        itemCode: itemCode || '',
        quantity,
        isEzShip: isEZShip,
        algoliaQueryID: [...new Set([algoliaQueryID || '', ...existingAlgoliaQueryID])].toString(),
      };

      if (isEZShip) {
        item.numberOfEZShipDays = 7;
      }

      if (webalias) {
        item.webAlias = webalias;
      }

      if (clientIpAddress) {
        item.clientIpAddress = clientIpAddress;
      }

      return await addToCart(item)
        .unwrap()
        .then(async res => {
          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            toast(res.message || res.exceptionMessage, { type: 'success' });
          }

          return res;
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });

          return err as any;
        });
    },
    [addToCart, cart, webalias]
  );

  const handleAddProductsListToCart = useCallback(
    async (products: ProductStrapi[], quantity: number, isEZShip: boolean, algoliaQueryID?: string) => {
      const clientIpAddress = Cookie.get('clientIpAddress');

      if (!products || products.length === 0) return;

      const itemList: AddToCart[] = products
        .map(product => {
          const itemCode = product?.attributes?.ItemCode;
          if (!itemCode) return null;

          const existingCartItem = cart?.find(c => c.id === itemCode);
          const existingAlgoliaQueryID = existingCartItem?.algoliaQueryID?.split(',') || [];

          const item: AddToCart = {
            itemCode,
            quantity,
            isEzShip: isEZShip,
            algoliaQueryID: [...new Set([algoliaQueryID || '', ...existingAlgoliaQueryID])].toString(),
          };

          if (isEZShip) {
            item.numberOfEZShipDays = 7;
          }

          if (webalias) {
            item.webAlias = webalias;
          }

          if (clientIpAddress) {
            item.clientIpAddress = clientIpAddress;
          }

          return item;
        })
        .filter(Boolean) as AddToCart[]; // Filter out any nulls

      try {
        const response = await addListToCart(itemList).unwrap();

        // Optionally handle response
        // toast(response.message, { type: 'success' });
      } catch (err: any) {
        const errorMessage =
          err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage ?? 'Failed to add products to cart';
        toast(errorMessage, { type: 'error' });
      }
    },
    [addListToCart, cart, webalias]
  );

  const handleAddProductsListToCartForEnrollment = useCallback(
    async (products: ProductStrapiOfEnrollment[], isEZShip: boolean, algoliaQueryID?: string) => {
      const clientIpAddress = Cookie.get('clientIpAddress');

      if (!products || products.length === 0) return;

      const itemList: AddToCart[] = products
        .map(entry => {
          // ✅ Normalize product shape
          const product = (entry as any).product ?? entry;

          const itemCode = product?.attributes?.ItemCode;
          if (!itemCode) return null;

          const existingCartItem = cart?.find(c => c.id === itemCode);
          const existingAlgoliaQueryID = existingCartItem?.algoliaQueryID?.split(',') ?? [];

          const item: AddToCart = {
            itemCode,
            quantity: product.quantity ?? 1,
            isEzShip: isEZShip,
            algoliaQueryID: [...new Set([algoliaQueryID, ...existingAlgoliaQueryID].filter(Boolean))].join(','),
          };

          if (isEZShip) {
            item.numberOfEZShipDays = 7;
          }

          if (webalias) {
            item.webAlias = webalias;
          }

          if (clientIpAddress) {
            item.clientIpAddress = clientIpAddress;
          }

          return item;
        })
        .filter(Boolean) as AddToCart[];

      try {
        await addListToCart(itemList).unwrap();
      } catch (err: any) {
        const errorMessage =
          err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage ?? 'Failed to add products to cart';
        toast(errorMessage, { type: 'error' });
      }
    },
    [addListToCart, cart, webalias]
  );

  const handleAddToCartUsingPawPoints = useCallback(
    async (product: ProductStrapi, quantity: number, isEZShip: boolean, algoliaQueryID?: string) => {
      const clientIpAddress = Cookie.get('clientIpAddress');
      const existingCartItem = cart?.find(c => c.id === product?.attributes?.ItemCode);
      const existingAlgoliaQueryID = existingCartItem?.algoliaQueryID?.split(',') || [];
      const item: AddToCart = {
        itemCode: product?.attributes?.ItemCode || '',
        quantity,
        isEzShip: isEZShip,
        algoliaQueryID: [...new Set([algoliaQueryID || '', ...existingAlgoliaQueryID])].toString(),
      };

      if (isEZShip) {
        item.numberOfEZShipDays = 7;
      }

      if (webalias) {
        item.webAlias = webalias;
      }

      if (clientIpAddress) {
        item.clientIpAddress = clientIpAddress;
      }

      // setModalConfig(state => ({ ...state, isOpen: false }));

      await addToCartUsingPawPoints(item)
        .unwrap()
        .then(async res => {
          // setKlaviyoCartData(mapDataToAddedToCart(cart || [], cartItem, window.location.origin));
          const toastData: AddToCartToast = {
            name: product.attributes.ItemName,
            image: product.attributes.MainImage,
            quantity: item.quantity,
            weight: product.attributes.Weight,
            price: product.attributes.Prices?.RetailPrice ?? 0,
            pawPoints: product?.attributes.Prices?.RetailPawPointsPrice ?? 0,
            paymentType: PaymentType.PAW_POINT,
          };

          const newResponse: SuccessResponse<AddToCartToast> = {
            message: res.message,
            exceptionMessage: res.exceptionMessage,
            success: res.success,
            data: toastData,
          };

          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            addToCartNotification.current = newResponse;
          }

          // if (res.success) {
          //   const AddedToCartPopup = (await import('@/components/Popup/AddedToCart')).default;
          //   toast(({ closeToast }) => <AddedToCartPopup closeToast={closeToast} product={product} />, {
          //     containerId: ADD_TO_CART_TOAST,
          //   });
          // } else {
          //   toast(res.message, { type: 'error' });
          // }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [addToCartUsingPawPoints, cart, webalias]
  );

  const handleAddToCartUsingSampleCredits = useCallback(
    async (product: ProductStrapi, quantity: number, isEZShip: boolean, algoliaQueryID?: string) => {
      const clientIpAddress = Cookie.get('clientIpAddress');
      const existingCartItem = cart?.find(c => c.id === product?.attributes?.ItemCode);
      const existingAlgoliaQueryID = existingCartItem?.algoliaQueryID?.split(',') || [];
      const item: AddToCart = {
        itemCode: product?.attributes?.ItemCode || '',
        quantity,
        isEzShip: isEZShip,
        algoliaQueryID: [...new Set([algoliaQueryID || '', ...existingAlgoliaQueryID])].toString(),
      };

      if (isEZShip) {
        item.numberOfEZShipDays = 7;
      }

      if (webalias) {
        item.webAlias = webalias;
      }

      if (clientIpAddress) {
        item.clientIpAddress = clientIpAddress;
      }
      // setModalConfig(state => ({ ...state, isOpen: false }));

      await addToCartUsingSampleCredits(item)
        .unwrap()
        .then(async res => {
          // setKlaviyoCartData(mapDataToAddedToCart(cart || [], cartItem, window.location.origin));
          const toastData: AddToCartToast = {
            name: product.attributes.ItemName,
            image: product.attributes.MainImage,
            quantity: item.quantity,
            weight: product.attributes.Weight,
            price: product.attributes.Prices?.RetailPrice ?? 0,
            sampleCredits: product?.attributes?.Prices?.RetailSampleCreditsPrice ?? 0,
            paymentType: PaymentType.SAMPLE_CREDIT,
          };

          const newResponse: SuccessResponse<AddToCartToast> = {
            message: res.message,
            exceptionMessage: res.exceptionMessage,
            success: res.success,
            data: toastData,
          };

          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            addToCartNotification.current = newResponse;
          }

          // if (res.success) {
          //   const AddedToCartPopup = (await import('@/components/Popup/AddedToCart')).default;
          //   toast(({ closeToast }) => <AddedToCartPopup closeToast={closeToast} product={product} />, {
          //     containerId: ADD_TO_CART_TOAST,
          //   });
          // } else {
          //   toast(res.message, { type: 'error' });
          // }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [addToCartUsingSampleCredits, cart, webalias]
  );

  const handleUpdateQuantity = useCallback(
    async (id: string, quantity: number) => {
      await updateQuantity({ id, quantity })
        .unwrap()
        .then(res => {
          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            notification.current = res;
          }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [updateQuantity]
  );

  const handleUpdateEZShip = useCallback(
    async (id: string, isEZShip: boolean) => {
      await updateEZShip({ id, type: isEZShip ? 1 : 0 })
        .unwrap()
        .then(res => {
          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            notification.current = res;
          }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [updateEZShip]
  );

  const handleUpdateListOfProductEZShip = useCallback(
    async (itemList: { ids: string[]; isEZShip: boolean }) => {
      try {
        const res = await updateListEZShip({
          ids: itemList.ids,
          type: itemList.isEZShip ? 1 : 0,
        }).unwrap();
        if (!res.success) {
          toast(res.message || res.exceptionMessage || 'Unknown error', { type: 'error' });
        } else {
          notification.current = res;
        }
      } catch (err: any) {
        const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
        toast(errorMessage, { type: 'error' });
      }
    },
    [updateEZShip]
  );

  const handleRemoveFromCart = useCallback(
    async (id: string) => {
      await removeFromCart({ id })
        .unwrap()
        .then(res => {
          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            notification.current = res;
          }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [removeFromCart]
  );

  const handleRemoveListFromCart = useCallback(
    async (ids: string[], removeForcefully?: boolean) => {
      await removeListFromCart({ ids, removeForcefully })
        .unwrap()
        .then(res => {
          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            notification.current = res;
          }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [removeListFromCart]
  );

  const handleApplyFoodLabel = useCallback(
    async (payload: ApplyFoodLabelPayload) => {
      if (ezShipId) {
        await applyFoodLabelToEzShipItem({
          autoOrderID: ezShipId,
          isActive: true,
          itemCode: payload.itemCode,
          labelID: payload.labelID,
          petSpecieID: payload.petSpecieID,
        })
          .unwrap()
          .then(res => {
            toast(res.message, { type: res.success ? 'success' : 'error' });
          })
          .catch(err => {
            const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
            toast(errorMessage, { type: 'error' });
          });
        return;
      }

      await applyFoodLabel(payload)
        .unwrap()
        .then(res => {
          if (!res.success) {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            notification.current = res;
          }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [applyFoodLabel, applyFoodLabelToEzShipItem, ezShipId]
  );

  const handleAddItemToAutoOrder = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (payload: AddToEzshipPayload) => {
      setVisibilityOfEzShipSidebar(true);

      dispatch(setAction({ name: 'addToEzShip' }));

      const items = payload.items;
      const algoliaIDsMap: Record<string, string> = {};

      items.forEach(item => {
        const foundItem = [...(currentEzShip?.addedItems || []), ...(currentEzShip?.existingItems || [])]?.find(
          ai => ai.itemCode == item.itemCode
        );

        const algoliaIDsExisting = foundItem?.algoliaQueryID?.split(',') || [];
        const algoliaIDCurrent = item.algoliaQueryID || '';

        const combinedAlgoliaIDs = [algoliaIDCurrent, ...algoliaIDsExisting].toString();
        algoliaIDsMap[item.itemCode] = combinedAlgoliaIDs;
      });

      await addToEzship({
        ...payload,
        items: payload.items.map(i => ({ ...i, algoliaQueryID: algoliaIDsMap[i.itemCode] })),
      })
        .unwrap()
        .then(res => {
          if (!res.success) {
            dispatch(removeAction());

            toast(res.message || res.exceptionMessage, { type: 'error' });
          } else {
            toast(res.message, { type: 'success' });

            const { data } = res;
            const autoOrderID = data.autoOrderID;

            if (payload.autoOrderID === autoOrderID) return;
            const searchPageRegex = /\/(products)\/(search)/;
            const productListingRegex = /\/(products)\/(.*)/;
            const productDetailRegex = /\/(product)\/(.*)/;
            const categoryListingRegex = /(\/products\/ezship\/(.*))/;
            const matchCategoryListingPage = pathName.match(categoryListingRegex);
            const matchProductListingPage = pathName.match(productListingRegex);
            const matchProductDetailPage = pathName.match(productDetailRegex);
            const matchSearchPage = pathName.match(searchPageRegex);

            let neewURL = ``;

            if (matchCategoryListingPage) {
              neewURL = generateApproperiateURL(`/products/ezship/${autoOrderID}`, webalias);
            } else if (matchProductListingPage) {
              neewURL = `${pathName}?autoOrderID=${autoOrderID}`;
            } else if (matchProductDetailPage) {
              const chunk = matchProductDetailPage[2].split('/').pop() || '';
              const parsedChunk =
                chunk.toLowerCase().startsWith('/ezship') || chunk.toLowerCase().startsWith('ezship')
                  ? chunk.split('/').pop()
                  : chunk;

              console.log(matchProductDetailPage, 'matchProductDetailPage', parsedChunk);
              neewURL = generateApproperiateURL(`/product/ezship/${autoOrderID}/${parsedChunk}`, webalias);
            } else if (matchSearchPage) {
              neewURL = `${pathName}${pathName.includes('?') ? '&' : '?'}autoOrderID=${autoOrderID}`;
            }
            if (neewURL) {
              // dispatch(setEzShipConfig({ isNewEzShip: true, ezShipId: autoOrderID }));
              const current = new URLSearchParams(
                Array.from(searchParams.entries()).filter(v => v[0] && v[0]?.toLowerCase() !== 'autoorderid')
              );
              window.history.replaceState(
                null,
                '',
                `${neewURL}${
                  neewURL?.includes('?')
                    ? (neewURL.match(/^\?.+/) ? '&' : '') + current.toString()
                    : '?' + current.toString()
                }`
              );
            }
          }
        })
        .catch((err: ResponseError) => {
          dispatch(removeAction());

          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [
      setVisibilityOfEzShipSidebar,
      dispatch,
      addToEzship,
      currentEzShip?.addedItems,
      currentEzShip?.existingItems,
      pathName,
      webalias,
      router,
    ]
  );

  const handleUpdateEzShipCartQuantity = useCallback(
    async (payload: UpdateProductQuantityInAutoOrderPayload) => {
      dispatch(setAction({ name: 'updateEzShipCartQuantity', id: payload.itemCode }));

      await updateEzShipCartQuantity(payload)
        .unwrap()
        .then(res => {
          if (!res.success) {
            dispatch(removeAction());
          }
          toast(res.message || res.exceptionMessage, { type: !res.success ? 'error' : 'success' });
        })
        .catch((err: ResponseError) => {
          dispatch(removeAction());

          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [updateEzShipCartQuantity, dispatch]
  );

  const handleRemoveItemFromAutoOrder = useCallback(
    async (payload: { autoOrderId: string; itemCode: string }) => {
      dispatch(setAction({ name: 'removeEzShipFromCart', id: payload.itemCode }));

      await removeFromEzShip(payload)
        .unwrap()
        .then(res => {
          if (!res.success) {
            dispatch(removeAction());
          }
          toast(res.message || res.exceptionMessage, { type: !res.success ? 'error' : 'success' });
        })
        .catch((err: ResponseError) => {
          dispatch(removeAction());

          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [removeFromEzShip, dispatch]
  );

  const handleAddToExistingEzShip = useCallback(
    async (
      payload: AddToExistingEzShipPayload,
      product: ProductStrapi,
      autoOrderTitle: string | undefined,
      success: () => void
    ) => {
      await addToExistingEzShip(payload)
        .unwrap()
        .then(res => {
          if (res.success) {
            success();
            toast(
              ({ closeToast }) => (
                <AddedToExistingEzShipPopup
                  closeToast={closeToast}
                  productName={product.attributes.ItemName}
                  ezShipTitle={autoOrderTitle || ''}
                  ezShipId={payload.autoOrderID}
                />
              ),
              {
                containerId: ADD_TO_CART_TOAST,
                position: 'top-right',
                autoClose: false,
              }
            );
          } else {
            toast(res.message || res.exceptionMessage, { type: 'error' });
          }
        })
        .catch((err: ResponseError) => {
          const errorMessage = err?.data?.title ?? err?.data?.message ?? err?.data?.exceptionMessage;
          toast(errorMessage, { type: 'error' });
        });
    },
    [addToExistingEzShip]
  );

  // useEffect(() => {
  //   if (!isEzshipCartLoading) {
  //     setAction(prev => ({ ...prev, name: '', payload: null }));
  //   }
  // }, [isEzshipCartLoading]);

  useEffect(() => {
    if (!isLoadingShoppingCart && notification.current) {
      toast(notification.current.message, { type: 'success' });
    }
    notification.current = null;
  }, [isLoadingShoppingCart]);

  useEffect(() => {
    if (!isLoadingShoppingCart && addToCartNotification.current) {
      const product = addToCartNotification.current?.data;

      toast(
        ({ closeToast }) => (
          <AddedToCartPopup
            totalItemsInCart={totalItemsInCart}
            closeToast={closeToast}
            product={product as AddToCartToast}
          />
        ),
        {
          containerId: ADD_TO_CART_TOAST,
        }
      );
    }
    addToCartNotification.current = null;
  }, [isLoadingShoppingCart, totalItemsInCart]);

  const memoizedData = useMemo(() => {
    return {
      totalItemsInCart,
      totalPrice,
      totalPawPoints,
      totalSampleCredits,
      hasItemWithSampleCredits,
      hasItemWithPawPoints,
      ezShipItems,
      totalEZShipItems,
      ezShipTotalPrice,
      qualifiedItemsCount,
      cart,
      giftCardNumber,
      couponCode,
      isAddingToCart,
      isAddingToCartUsingPawPoints,
      isAddingToCartUsingSampleCredits,
      isUpdatingEZShip,
      isUpdatingQuantity,
      isRemovingFromCart,
      isApplyingLabel,
      isAddingToExistingEzShip,
      isLoadingShoppingCart,
      isLoading,
      isUpdatingEzShipCartQuantity,
      isCartStable,
      showChooseYourCheckoutFlowModal,
      isEnrollmentFlow,
      onAddToCart: handleAddToCart,
      onAddToCartWithItemCode: handleAddToCartWithItemCode,
      onAddToCartUsingPawPoints: handleAddToCartUsingPawPoints,
      onAddToCartUsingSampleCredits: handleAddToCartUsingSampleCredits,
      onUpdateQuantity: handleUpdateQuantity,
      onUpdateEZShip: handleUpdateEZShip,
      onRemoveFromCart: handleRemoveFromCart,
      onApplyFoodLabel: handleApplyFoodLabel,
      onAddToAutoOrder: handleAddItemToAutoOrder,
      onUpdateEzShipCartQuantity: handleUpdateEzShipCartQuantity,
      onRemoveFromEzShip: handleRemoveItemFromAutoOrder,
      onAddToExistingEzShip: handleAddToExistingEzShip,
      onRefetchEzShipCartItems: refetchEzShipCartItems,
      onAddProductsListToCart: handleAddProductsListToCart,
      onAddProductsListToCartForEnrollment: handleAddProductsListToCartForEnrollment,
      onRemoveListFromCart: handleRemoveListFromCart,
      onUpdateListOfProductEZShip: handleUpdateListOfProductEZShip,
      setShowChooseYourCheckoutFlowModal,
      shoppingFlowType,
      updateShoppingFlowType,
    };
  }, [
    cart,
    giftCardNumber,
    couponCode,
    totalItemsInCart,
    totalPrice,
    totalPawPoints,
    totalSampleCredits,
    hasItemWithSampleCredits,
    hasItemWithPawPoints,
    totalEZShipItems,
    ezShipItems,
    ezShipTotalPrice,
    qualifiedItemsCount,
    isAddingToCart,
    isAddingToCartUsingPawPoints,
    isAddingToCartUsingSampleCredits,
    isUpdatingEZShip,
    isUpdatingQuantity,
    isRemovingFromCart,
    isApplyingLabel,
    isAddingToExistingEzShip,
    isLoading,
    isCartStable,
    isLoadingShoppingCart,
    isUpdatingEzShipCartQuantity,
    showChooseYourCheckoutFlowModal,
    isEnrollmentFlow,
    handleAddToCart,
    handleAddToCartWithItemCode,
    handleAddToCartUsingPawPoints,
    handleAddToCartUsingSampleCredits,
    handleUpdateQuantity,
    handleUpdateEZShip,
    handleRemoveFromCart,
    handleApplyFoodLabel,
    handleAddItemToAutoOrder,
    handleRemoveItemFromAutoOrder,
    handleAddToExistingEzShip,
    handleUpdateEzShipCartQuantity,
    refetchEzShipCartItems,
    handleAddProductsListToCart,
    handleAddProductsListToCartForEnrollment,
    handleRemoveListFromCart,
    handleUpdateListOfProductEZShip,
    setShowChooseYourCheckoutFlowModal,
    shoppingFlowType,
    updateShoppingFlowType,
  ]);

  return <ShoppingCartContext.Provider value={memoizedData}>{children}</ShoppingCartContext.Provider>;
};

export const useShoppingCartContext = () => useContext(ShoppingCartContext) || ({} as ShoppingCartContextProps);

export default ShoppingCartProvider;
